import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, ChevronUp, Eye, Pencil, Trash2, X } from 'lucide-react';
import { ContentChannel } from '@mg/shared';
import { ApprovalState } from '@mg/shared';
import type { ApprovalType, Post } from '@mg/shared';
import { cn } from '../../lib/cn';
import { channelLabel, displayStateLabel, displayStateTone, type PostDisplayState , formatMetric, formatRelative, metricsPending } from '../../lib/display';
import { Badge } from '../../components/ui/Badge';
import { useEdgeBleed } from '../../components/ui/useEdgeBleed';
import { CategoryPill } from '../categories/CategoryPill';
import { PostCreatorAvatar } from './PostCreatorAvatar';
import { PostThumbnail } from './PostThumbnail';
import { PlatformIcon } from './PlatformIcon';
import { postTypeIcon } from './postTypes';
import { PLATFORM_META } from '../social/platforms';
import { PostMenu, type PostCardActions } from './PostCard';
import {
  compareListNames,
  compareMissingLast,
  compareNames,
  defaultOrder,
  postCampaignName,
  postListName,
  publishedSortTime,
  scheduleDistance,
  scheduledSortTime,
  updatedSortTime,
  type PostsSortProfile,
  type SortDir,
} from './postsOrder';

/** "MM/DD/YYYY hh:mm AM" for the Published-date column (mirrors the wireframe). */
function formatPublishedCell(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d
    .toLocaleString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    })
    .replace(',', '');
}

/** The engagement metrics we surface, in column order. */
type MetricKey = 'views' | 'likes' | 'comments' | 'shares';
const METRIC_COLUMNS: { key: MetricKey; label: string }[] = [
  { key: 'views', label: 'Views' },
  { key: 'likes', label: 'Likes' },
  { key: 'comments', label: 'Comments' },
  { key: 'shares', label: 'Shares' },
];


/**
 * The number a metric column sorts on, or null for "we have no number". An unpublished post, a
 * published post we have never fetched metrics for, and a metric the platform withholds are all
 * unknown — deliberately NOT 0, so none of them can outrank a post that genuinely scored zero.
 */
function metricSortValue(post: Post, key: MetricKey): number | null {
  if (post.publishedAt == null) return null;
  if (post.metrics?.fetchedAt == null) return null;
  return post.metrics[key];
}

/** One engagement cell: dash when there is nothing to show, shimmer while we haven't asked yet. */
function MetricValue({ post, metric }: { post: Post; metric: MetricKey }) {
  // Only a published post can have engagement. Dash rather than blank so every row keeps the
  // same height and nobody reads an empty cell as zero.
  if (post.publishedAt == null) return <span className="text-ink-ghost">—</span>;
  // Only a MEASURABLE post that we haven't asked about yet shimmers. `metrics == null` means the
  // post has no platform id and never can be measured (everything published before we started
  // recording ids) — that must settle to a dash, not shimmer forever.
  if (metricsPending(post))
    return (
      <>
        <span
          className="inline-block h-4 w-10 animate-pulse rounded bg-chip align-middle"
          aria-hidden
        />
        <span className="sr-only">Pending</span>
      </>
    );
  const value = post.metrics?.[metric] ?? null;
  return value == null ? <span className="text-ink-ghost">—</span> : <>{formatMetric(value)}</>;
}

/** The Platform cell: a branded icon + network name for social, else the post type. */
function PlatformCell({ post }: { post: Post }) {
  if (post.channel === ContentChannel.SOCIAL && post.platform) {
    return (
      <span className="inline-flex items-center gap-1.5 truncate text-ink-label">
        <PlatformIcon platform={post.platform} size="sm" />
        <span className="truncate">{PLATFORM_META[post.platform]?.label ?? 'Social'}</span>
      </span>
    );
  }
  const Icon = postTypeIcon[post.channel];
  return (
    <span className="inline-flex items-center gap-1.5 truncate text-ink-label">
      <span className="inline-flex h-5 w-5 shrink-0 items-center justify-center">
        <Icon className="h-4 w-4 text-ink-faint" aria-hidden />
      </span>
      <span className="truncate">{channelLabel[post.channel]}</span>
    </span>
  );
}

/** What a list column can be sorted on: the post's name, its campaign, a date, or a metric. */
export type PostsSortKey = 'name' | 'campaign' | 'published' | 'scheduled' | 'updated' | MetricKey;
/** The column a list is sorted by, and which way. */
export type PostsSort = { key: PostsSortKey; dir: SortDir };

/**
 * Per kind of Status tab (postsOrder's PostsSortProfile, picked by PostsView): the sort the list opens
 * on, shown as the active sort on that column, and the columns that sort.
 *
 * - published: Published date, newest first. Post, Campaign, Published date and the four metrics.
 * - schedule (In Review, Revise, Scheduled, Failed, Rejected): Scheduled date, nearest to today
 *   first. Post, Campaign and Scheduled date.
 * - drafts: Updated, newest first. Post, Campaign, Scheduled date and Updated.
 *
 * The rows each one opens with come from postsOrder's `defaultOrder`, the order the tile layout draws,
 * so a tab reads the same in both layouts until a column is picked.
 */
const SORT_PROFILES: Record<PostsSortProfile, { opensOn: PostsSort; columns: readonly PostsSortKey[] }> = {
  published: {
    opensOn: { key: 'published', dir: 'desc' },
    columns: ['name', 'campaign', 'published', 'views', 'likes', 'comments', 'shares'],
  },
  schedule: { opensOn: { key: 'scheduled', dir: 'asc' }, columns: ['name', 'campaign', 'scheduled'] },
  drafts: { opensOn: { key: 'updated', dir: 'desc' }, columns: ['name', 'campaign', 'scheduled', 'updated'] },
};

/**
 * The direction a column opens on: A to Z for the post and campaign names, newest first for the
 * Published date and Updated, nearest to today first for the Scheduled date ('asc' is the smallest
 * distance from today; flipped, the furthest comes first), and Most to Least for a metric ("who
 * performed best" is the question an engagement column is asked). Clicking the active column flips it.
 */
const FIRST_DIR: Record<PostsSortKey, SortDir> = {
  name: 'asc',
  campaign: 'asc',
  published: 'desc',
  scheduled: 'asc',
  updated: 'desc',
  views: 'desc',
  likes: 'desc',
  comments: 'desc',
  shares: 'desc',
};

const byNumber = (a: number, b: number) => a - b;

/**
 * How a column orders two rows. A row with nothing to sort on (no campaign, no date, no number) goes
 * last in BOTH directions (compareMissingLast). `now` is read once per sort by the caller, so "nearest
 * to today" cannot move between two comparisons.
 */
function compareColumn({ key, dir }: PostsSort, now: number): (a: Post, b: Post) => number {
  switch (key) {
    case 'name':
      return (a, b) => (dir === 'asc' ? compareListNames(a, b) : compareListNames(b, a));
    case 'campaign':
      return (a, b) => compareMissingLast(postCampaignName(a), postCampaignName(b), dir, compareNames);
    case 'published':
      return (a, b) => compareMissingLast(publishedSortTime(a), publishedSortTime(b), dir, byNumber);
    case 'scheduled':
      return (a, b) => compareMissingLast(scheduleDistance(a, now), scheduleDistance(b, now), dir, byNumber);
    case 'updated':
      return (a, b) => compareMissingLast(updatedSortTime(a), updatedSortTime(b), dir, byNumber);
    default:
      return (a, b) => compareMissingLast(metricSortValue(a, key), metricSortValue(b, key), dir, byNumber);
  }
}

/** What a sorted header says about its rows, in ARIA's aria-sort words ('other': neither way). */
type RowsRun = 'ascending' | 'descending' | 'other';

/**
 * Which way a column's values run down the list: 'ascending' when they never go down, 'descending'
 * when they never go up, 'other' when they do both. Null when they cannot tell, because fewer than
 * two rows have a value or every value is the same: then the direction picked is as true as any.
 */
function howValuesRun(values: readonly number[]): RowsRun | null {
  let rises = false;
  let falls = false;
  for (let i = 1; i < values.length; i++) {
    if (values[i] > values[i - 1]) rises = true;
    else if (values[i] < values[i - 1]) falls = true;
  }
  if (rises && falls) return 'other';
  if (rises) return 'ascending';
  if (falls) return 'descending';
  return null;
}

/** A sortable column header — click to sort, with the direction shown on the active column. */
function SortableTh({
  label,
  className,
  sort,
  sortKey,
  onSort,
  runs = null,
}: {
  label: string;
  className: string;
  sort: PostsSort | null;
  sortKey: PostsSortKey;
  onSort: (key: PostsSortKey) => void;
  /**
   * How the rows under this header actually run while it is the sort, read from them by the caller:
   * for a column that does not sort by the values it shows (Scheduled date sorts by distance from
   * today). Null or absent: the direction picked says it.
   */
  runs?: RowsRun | null;
}) {
  const activeDir = sort?.key === sortKey ? sort.dir : null;
  const said: RowsRun | undefined = activeDir
    ? (runs ?? (activeDir === 'asc' ? 'ascending' : 'descending'))
    : undefined;
  // The arrow agrees with what the header says; for rows that run neither way it shows the
  // direction picked, so a click still visibly turns it.
  const Chevron = (said === 'other' ? activeDir === 'asc' : said === 'ascending') ? ChevronUp : ChevronDown;
  return (
    <th
      className={className}
      // Only the sorted column carries aria-sort (ARIA: one header at a time); the others have none.
      aria-sort={said}
    >
      <button
        type="button"
        onClick={() => onSort(sortKey)}
        // Preflight gives a <button> `text-transform: none`; inherit the header's uppercase so a
        // sortable header reads like the plain ones beside it.
        className="focus-ring group inline-flex items-center gap-1 rounded [text-transform:inherit] hover:text-ink-strong"
      >
        {label}
        {/* Always rendered (invisible when idle) so the header keeps its width and the column
            can't jump as the sort moves between columns. */}
        <Chevron
          className={cn(
            'h-3 w-3 shrink-0',
            activeDir ? 'text-ink-label' : 'text-ink-faint opacity-0 group-hover:opacity-100',
          )}
          aria-hidden
        />
      </button>
    </th>
  );
}

/** How a list sorts: its tab's profile, the column chosen on that tab, and how to change it. */
export interface PostsTableSorting {
  /** The kind of tab the list shows: the order it opens in and the columns that sort. */
  profile: PostsSortProfile;
  /** The column chosen on this tab, or null for the tab's own order. */
  sort: PostsSort | null;
  /** Records a new choice for this tab. */
  onSortChange: (sort: PostsSort) => void;
}

interface PostsTableProps {
  posts: Post[];
  approvalType: ApprovalType;
  /** The owning campaign's aggregate state — governs the ⋯ release menu. */
  campaignStateFor: (post: Post) => ApprovalState;
  /** The mode-aware state the status badge should show. */
  badgeStateFor: (post: Post) => PostDisplayState;
  /** Per-post edit/schedule/publish/duplicate/move/delete handlers (same object PostCard uses). */
  actionsFor: (post: Post) => PostCardActions;
  /** Bulk-delete the given post ids (the caller confirms + invalidates). */
  onDeleteMany: (ids: string[]) => void;
  /**
   * Absent, the list keeps the order it is handed and no header sorts: Ready to Schedule, and any
   * other caller.
   */
  sorting?: PostsTableSorting;
}

/**
 * The "All Posts" LIST layout as a data table (Post · Platform · Scheduling category · Campaign ·
 * Status · Scheduled date · Updated · User, with Published date · Views/Likes/Comments/Shares in
 * place of Scheduled date · Updated on the Published tab, + a per-row edit/⋯ menu and a
 * bulk-select checkbox column). Scheduling category and User are display only and show on every
 * tab at every width.
 * Uses responsive column hiding inside its own horizontal scroller, so the page never scrolls
 * horizontally and the row ⋯ menu (portaled) is never clipped. Selection is by post id and
 * self-heals when the filters change (ids no longer visible drop out of the bulk bar + delete
 * set) — and survives a re-sort for the same reason.
 */
export function PostsTable({
  posts,
  approvalType,
  campaignStateFor,
  badgeStateFor,
  actionsFor,
  onDeleteMany,
  sorting,
}: PostsTableProps) {
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());

  // Only ids still in the current (filtered) list count — so switching filters/tabs quietly drops
  // hidden selections from the bulk bar and the delete set.
  const selectedVisible = useMemo(
    () => posts.filter((p) => selected.has(p.id)).map((p) => p.id),
    [posts, selected],
  );
  const allChecked = posts.length > 0 && selectedVisible.length === posts.length;
  const someChecked = selectedVisible.length > 0 && !allChecked;

  const toggleAll = () =>
    setSelected(allChecked ? new Set() : new Set(posts.map((p) => p.id)));
  const toggleOne = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  /**
   * Engagement columns only make sense for posts that have actually gone out, so the list swaps
   * its trailing columns based on what it is showing: Published date + Views/Likes/Comments/Shares
   * for a published set, Scheduled date + Updated for anything else.
   *
   * Keyed off the STATUS EACH ROW DISPLAYS, not `publishedAt`. Those diverge: in CAMPAIGN approval
   * mode the badge follows the CAMPAIGN's state, so a campaign still marked Scheduled can hold a
   * post that has already gone out. Under `some()` that one row gave the whole Scheduled tab the
   * engagement layout — every row headed "Published date" over a schedule, four columns of dashes,
   * and no Updated column. (The Failed tab was never affected: a post with a publish time is by
   * definition not failed.)
   *
   * `every` rather than `some` cannot strand a mixed set here, because the list is always one status
   * tab's worth of rows and the tab filters on this very badge — so on the Published tab every row
   * is PUBLISHED by construction, and on any other tab none is.
   */
  const showEngagement = useMemo(
    () => posts.length > 0 && posts.every((p) => badgeStateFor(p) === ApprovalState.PUBLISHED),
    [posts, badgeStateFor],
  );

  /**
   * The sort in force: the column chosen on this tab, else the tab's own order. The choice is the
   * caller's to keep, per tab (PostsView), because this table is not remounted when the Status tab
   * changes: a column picked on one tab must not reorder the next. A choice the tab's profile does not
   * offer falls back to the tab's own order instead of sorting by a column that shows no sort.
   */
  const profileId = sorting?.profile ?? null;
  const profile = profileId ? SORT_PROFILES[profileId] : null;
  const chosen = sorting?.sort ?? null;
  const activeSort: PostsSort | null = profile
    ? chosen && profile.columns.includes(chosen.key)
      ? chosen
      : profile.opensOn
    : null;
  const sortKey = activeSort?.key ?? null;
  const sortDir = activeSort?.dir ?? null;

  // The tab's own order first (postsOrder's defaultOrder, the order the tile layout draws), then the
  // chosen column, if it is another. Array sort is stable, so rows that tie on the chosen column, and
  // the whole block of rows with nothing to sort on, keep the tab's own order. One clock reading
  // serves the whole sort.
  const rows = useMemo(() => {
    if (!profileId || !sortKey || !sortDir) return posts;
    const now = Date.now();
    const ownOrder = [...posts].sort(defaultOrder(profileId, now));
    const { opensOn } = SORT_PROFILES[profileId];
    if (sortKey === opensOn.key && sortDir === opensOn.dir) return ownOrder;
    return ownOrder.sort(compareColumn({ key: sortKey, dir: sortDir }, now));
  }, [posts, profileId, sortKey, sortDir]);

  /**
   * How the rows run under the Scheduled date header while it is the sort. That column sorts by
   * distance from today, so its rows read as ascending dates only while every date is still to come:
   * the missed dates on Failed run newest first, and dates on both sides of today run neither way.
   * So the header says what the rows show, read from the dates their cells show (the undated rows,
   * always last, aside), and not the direction picked.
   */
  const scheduledRuns = useMemo(
    () =>
      sortKey === 'scheduled'
        ? howValuesRun(rows.map(scheduledSortTime).filter((time): time is number => time != null))
        : null,
    [rows, sortKey],
  );

  // A fresh column opens on its FIRST_DIR; clicking the active one (the tab's own column, before
  // any click) flips it.
  const toggleSort = (key: PostsSortKey) => {
    if (!sorting || !activeSort) return;
    sorting.onSortChange(
      activeSort.key === key
        ? { key, dir: activeSort.dir === 'desc' ? 'asc' : 'desc' }
        : { key, dir: FIRST_DIR[key] },
    );
  };

  /** A header that sorts when the tab's profile offers its column, else a plain one. */
  const header = (label: string, key: PostsSortKey, className: string) =>
    profile?.columns.includes(key) ? (
      <SortableTh
        key={key}
        label={label}
        className={className}
        sort={activeSort}
        sortKey={key}
        onSort={toggleSort}
        runs={key === 'scheduled' ? scheduledRuns : null}
      />
    ) : (
      <th key={key} className={className}>
        {label}
      </th>
    );

  /**
   * The column a tab opens sorted on shows at every width (the table scrolls in its own box): it is
   * how a narrow screen gets back to the tab's own order after sorting by another column. The
   * published list has always shown its date at every width. Every other column keeps its
   * breakpoint.
   */
  const opensOnKey = profile?.opensOn.key ?? null;
  const dateAtEveryWidth = showEngagement || opensOnKey === 'scheduled';
  const updatedAtEveryWidth = !showEngagement && opensOnKey === 'updated';

  const th = 'px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-ink-muted';
  /** Metric columns share the date column's plain numeric styling — no icons, no emphasis. */
  const metricTd = 'hidden whitespace-nowrap px-3 py-2.5 align-middle text-ink-label lg:table-cell';

  // The box the table scrolls in draws its own border and corners, so it is the box that runs to
  // the page's right edge when the table is too wide for it (useEdgeBleed's 'self' frame). The
  // table is not remounted when the Status tab changes: the hook decides again when the table's
  // rows or columns change, as well as when the table, its box or the page changes size.
  const bleedRef = useEdgeBleed<HTMLDivElement>();

  return (
    <div className="space-y-3">
      {selectedVisible.length > 0 && (
        <div className="flex items-center gap-2 rounded-lg border border-line bg-page px-3 py-2 text-sm">
          <span className="font-medium text-ink-strong">{selectedVisible.length} selected</span>
          <button
            type="button"
            onClick={() => onDeleteMany(selectedVisible)}
            className="focus-ring inline-flex items-center gap-1.5 rounded-md px-2 py-1 font-medium text-red-600 dark:text-error-dark hover:bg-red-50 dark:hover:bg-error-dark-tint"
          >
            <Trash2 className="h-4 w-4" aria-hidden />
            Delete
          </button>
          <button
            type="button"
            onClick={() => setSelected(new Set())}
            className="focus-ring ml-auto inline-flex items-center gap-1 rounded-md px-2 py-1 text-ink-muted hover:bg-chip"
          >
            <X className="h-4 w-4" aria-hidden />
            Clear
          </button>
        </div>
      )}

      {/* Own overflow-x scroller so a wide table scrolls INSIDE its box — the page never scrolls
          horizontally. The row ⋯ menu is portaled (PostMenu), so this wrapper's computed
          overflow-y:auto can't clip it. On a desktop screen a list wider than the page gives it
          runs this box to the page's right edge, its right border and corners dropped, so the
          cut at the window's edge says the table scrolls sideways (bleedRef). The table must
          stay this box's first child: it is the element the hook watches for size changes. */}
      <div ref={bleedRef} className="overflow-x-auto rounded-xl border border-line bg-card">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="border-b border-line-soft">
              <th className="w-10 px-3 py-2.5">
                <input
                  type="checkbox"
                  aria-label="Select all posts"
                  className="focus-ring h-4 w-4 rounded border-line-input text-action"
                  checked={allChecked}
                  ref={(el) => {
                    if (el) el.indeterminate = someChecked;
                  }}
                  onChange={toggleAll}
                />
              </th>
              <th className="w-14 px-2 py-2.5">
                <span className="sr-only">Thumbnail</span>
              </th>
              {/* Which of these sort is the tab's profile's to say (SORT_PROFILES): Post and Campaign
                  on every sorted tab, then its own date columns and, on Published, the metrics. */}
              {header('Post', 'name', th)}
              <th className={th}>Platform</th>
              {/* Scheduling category and User are display only: plain headers that never sort, on
                  every tab, at every width (the table scrolls in its own box). */}
              <th className={cn(th, 'whitespace-nowrap')}>Scheduling category</th>
              {header('Campaign', 'campaign', cn(th, 'hidden lg:table-cell'))}
              <th className={th}>Status</th>
              {showEngagement
                ? header('Published date', 'published', cn(th, 'whitespace-nowrap'))
                : header(
                    'Scheduled date',
                    'scheduled',
                    cn(th, 'whitespace-nowrap', !dateAtEveryWidth && 'hidden md:table-cell'),
                  )}
              {showEngagement
                ? METRIC_COLUMNS.map((col) =>
                    header(col.label, col.key, cn(th, 'hidden w-20 whitespace-nowrap lg:table-cell')),
                  )
                : header('Updated', 'updated', cn(th, 'whitespace-nowrap', !updatedAtEveryWidth && 'hidden sm:table-cell'))}
              {/* The last data column on every tab: after Updated, and after Shares on Published,
                  which has no Updated column. */}
              <th className={th}>User</th>
              <th className="w-16 px-3 py-2.5">
                <span className="sr-only">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((post) => {
              const actions = actionsFor(post);
              const badge = badgeStateFor(post);
              // publishedAt, not the badge — see PostCard: the badge lags in CAMPAIGN mode and is
              // derived in NONE mode, while this is exactly the set the composer opens read-only.
              const published = post.publishedAt != null;
              const isSel = selected.has(post.id);
              return (
                <tr
                  key={post.id}
                  className={cn(
                    'border-b border-line-soft last:border-b-0 hover:bg-hover',
                    isSel && 'bg-tint/40',
                  )}
                >
                  <td className="px-3 py-2.5 align-middle">
                    <input
                      type="checkbox"
                      aria-label={`Select ${post.title || 'post'}`}
                      className="focus-ring h-4 w-4 rounded border-line-input text-action"
                      checked={isSel}
                      onChange={() => toggleOne(post.id)}
                    />
                  </td>
                  <td className="px-2 py-2 align-middle">
                    <PostThumbnail
                      mediaKind={post.mediaKind}
                      thumbnailUrl={post.thumbnailUrl}
                      className="h-10 w-10 rounded-md"
                      iconClassName="h-4 w-4"
                    />
                  </td>
                  <td className="px-3 py-2.5 align-middle">
                    <button
                      type="button"
                      onClick={actions.onEdit}
                      className="focus-ring block max-w-[12rem] truncate text-left font-medium text-ink hover:text-link md:max-w-[20rem] xl:max-w-[32rem]"
                      title={postListName(post)}
                    >
                      {postListName(post)}
                    </button>
                  </td>
                  <td className="px-3 py-2.5 align-middle">
                    <PlatformCell post={post} />
                  </td>
                  <td className="px-3 py-2.5 align-middle">
                    {/* "General" when the post has no category: it queues into the General times. */}
                    <CategoryPill category={post.category} />
                  </td>
                  <td className="hidden px-3 py-2.5 align-middle lg:table-cell">
                    {post.campaignName ? (
                      // `from=posts` tells the campaign page which list to send you back to, so
                      // the back link reads "All posts" rather than dumping you on the campaign
                      // list you never came from. Survives a reload, unlike router state.
                      <Link
                        to={`/campaigns/${post.campaignId}?from=posts`}
                        className="focus-ring block max-w-[10rem] truncate rounded text-link hover:text-action-hover hover:underline"
                        title={post.campaignName}
                      >
                        {post.campaignName}
                      </Link>
                    ) : (
                      <span className="text-ink-ghost">—</span>
                    )}
                  </td>
                  <td className="px-3 py-2.5 align-middle">
                    <Badge tone={displayStateTone(badge)} size="sm">
                      {displayStateLabel(badge)}
                    </Badge>
                  </td>
                  <td
                    className={cn(
                      'whitespace-nowrap px-3 py-2.5 align-middle text-ink-label',
                      !dateAtEveryWidth && 'hidden md:table-cell',
                    )}
                  >
                    {/* Follows the header. Under "Scheduled date" the schedule wins even when a
                        publish time also exists — a failed release has both, and showing the moment
                        it went out under a column headed "Scheduled" is the wrong number and the
                        wrong label at once. Published rows are untouched. */}
                    {formatPublishedCell(
                      showEngagement
                        ? (post.publishedAt ?? post.scheduledAt)
                        : (post.scheduledAt ?? post.publishedAt),
                    )}
                  </td>
                  {showEngagement ? (
                    METRIC_COLUMNS.map((col) => (
                      <td key={col.key} className={metricTd}>
                        <MetricValue post={post} metric={col.key} />
                      </td>
                    ))
                  ) : (
                    <td
                      className={cn(
                        'whitespace-nowrap px-3 py-2.5 align-middle text-ink-muted',
                        !updatedAtEveryWidth && 'hidden sm:table-cell',
                      )}
                    >
                      {formatRelative(post.updatedAt)}
                    </td>
                  )}
                  <td className="px-3 py-2.5 align-middle">
                    {/* No name is printed, so the avatar carries it: its accessible label and hover tooltip.
                        In a flex box, as SourcesPage draws avatars: on a line of text a photo sits above the middle. */}
                    <div className="flex">
                      <PostCreatorAvatar person={post.createdBy} size="sm28" />
                    </div>
                  </td>
                  <td className="px-3 py-2.5 align-middle">
                    <div className="flex items-center justify-end gap-0.5">
                      {/* Published posts open read-only — an eye, not a pencil. */}
                      <button
                        type="button"
                        onClick={actions.onEdit}
                        aria-label={`${published ? 'View' : 'Edit'} ${post.title || 'post'}`}
                        className="focus-ring rounded-md p-1.5 text-ink-faint hover:bg-chip hover:text-ink-strong"
                      >
                        {published ? (
                          <Eye className="h-4 w-4" aria-hidden />
                        ) : (
                          <Pencil className="h-4 w-4" aria-hidden />
                        )}
                      </button>
                      <PostMenu
                        post={post}
                        campaignState={campaignStateFor(post)}
                        approvalType={approvalType}
                        actions={actions}
                      />
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
